import { useMemo, useState } from 'react';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DatePicker } from '../../components/DatePicker';
import { patchSubscription, ApiError } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { formatLocal } from '../../lib/time';
import type { SubscriptionWithUsage } from '../../lib/types';
import { orderByDateDescending } from '../../lib/admin-list-order';

interface Props {
  subscriptions: SubscriptionWithUsage[];
  onChanged: () => void;
}

export function SubscriptionsList({ subscriptions, onChanged }: Props) {
  const { push } = useToast();
  const [extendId, setExtendId] = useState<number | null>(null);
  const [extendDate, setExtendDate] = useState('');
  const [cancelId, setCancelId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const orderedSubscriptions = useMemo(
    () => orderByDateDescending(subscriptions, (subscription) => ({ date: subscription.starts_at, id: subscription.id })),
    [subscriptions],
  );

  const handleExtend = async () => {
    if (extendId == null || !extendDate) return;
    setBusyId(extendId);
    try {
      await patchSubscription(extendId, { expires_at: `${extendDate}T23:59:59+04:00` });
      push('success', `Subscription #${extendId} extended to ${extendDate}.`);
      setExtendId(null);
      setExtendDate('');
      onChanged();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Extend failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async () => {
    if (cancelId == null) return;
    setBusyId(cancelId);
    try {
      await patchSubscription(cancelId, { status: 'cancelled' });
      push('success', `Subscription #${cancelId} cancelled.`);
      setCancelId(null);
      onChanged();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Cancel failed');
    } finally {
      setBusyId(null);
    }
  };

  if (subscriptions.length === 0) {
    return (
      <div style={{ ...T.caption, color: PS.textSec, padding: 12 }}>
        No subscriptions.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {orderedSubscriptions.map((s) => {
        const total = s.minutes_total ?? s.hours_total * 60;
        const used = s.minutes_used ?? 0;
        const remaining = s.minutes_remaining ?? Math.max(0, total - used);
        const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
        const expired = new Date(s.expires_at) <= new Date();
        const statusColor =
          s.status === 'active' && !expired ? PS.success :
          s.status === 'cancelled' ? PS.danger :
          PS.textSec;
        return (
          <div
            key={s.id}
            style={{
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 4,
              padding: 12,
              background: PS.white,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ ...T.subheading }}>
                {s.plan} · {s.hours_total}h
                <span style={{ marginLeft: 8, ...T.label, color: statusColor }}>
                  {s.status}{expired && s.status === 'active' ? ' (expired)' : ''}
                </span>
              </div>
              <div style={{ ...T.caption, color: PS.textSec }}>
                #{s.id} · {fmtDate(s.starts_at)} → {fmtDate(s.expires_at)}
              </div>
            </div>

            <div
              style={{
                background: PS.surfaceDisabled,
                height: 8,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  background: pct >= 90 ? PS.warning : PS.tealDeep,
                  height: '100%',
                  transition: 'width 0.2s ease',
                }}
              />
            </div>
            <div style={{ ...T.caption, color: PS.textSec }}>
              {used} min used · {remaining} min remaining ({Math.floor(remaining / 60)}h{remaining % 60}m)
            </div>

            {s.status === 'active' && !expired && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setExtendId(s.id);
                    // Venue-local, not the UTC slice: this value is
                    // submitted, so an off-by-one here silently moves the expiry.
                    setExtendDate(formatLocal(s.expires_at, 'yyyy-MM-dd'));
                  }}
                  disabled={busyId === s.id}
                >
                  Extend…
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setCancelId(s.id)}
                  disabled={busyId === s.id}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {/* Extend dialog */}
      {extendId != null && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setExtendId(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: PS.white, padding: 20, borderRadius: 4, maxWidth: 360, width: '100%' }}
          >
            <h3 style={{ ...T.cardHeading, margin: '0 0 12px' }}>
              Extend subscription #{extendId}
            </h3>
            {/* DatePicker's root is a native <input type="date">, so this
                <label> is genuinely (implicitly) associated with it through
                the component boundary — eslint-plugin-jsx-a11y can't see
                through custom components to verify that, hence the disable
                below. ariaLabel gives the control its own explicit
                accessible name too. */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={T.label}>New expiry date</span>
              <DatePicker value={extendDate} onChange={setExtendDate} ariaLabel="New expiry date" />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button variant="secondary" onClick={() => setExtendId(null)}>Cancel</Button>
              <Button onClick={() => void handleExtend()} disabled={!extendDate || busyId === extendId}>
                Extend
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirm */}
      <ConfirmDialog
        open={cancelId !== null}
        title={`Cancel subscription #${cancelId ?? ''}?`}
        message="Sets status to cancelled. The user's remaining minutes are no longer usable for new bookings. Past bookings are unaffected."
        confirmLabel="Cancel subscription"
        destructive
        onConfirm={handleCancel}
        onCancel={() => setCancelId(null)}
      />
    </div>
  );
}

/**
 * Calendar date of a TIMESTAMPTZ instant, in the venue's timezone.
 *
 * Slicing the ISO string instead returns the **UTC** date. Tbilisi is UTC+4, so
 * anything between 20:00 and midnight local falls on the previous UTC day and
 * renders a day early — e.g. an expiry of 2026-08-01T21:00:00+00:00 is
 * 2026-08-02 01:00 at the club, but sliced to "2026-08-01".
 */
function fmtDate(iso: string): string {
  return formatLocal(iso, 'yyyy-MM-dd');
}
